import { useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

interface Props {
  trigger: ReactNode;
  children: ReactNode;
  accent?: string; // tailwind border color, e.g. "border-warning-border"
  widthPx?: number;
}

// Shared floating-card chrome for every custom hover tooltip in this
// feature (high-value star, reply status, ...) — one visual language
// (rounded card, accent border, small
// pointer arrow, fade+scale-in) instead of each icon growing its own
// bespoke positioning/animation code.
//
// Rendered into a portal (document.body) and positioned via
// getBoundingClientRect rather than plain CSS `absolute` inside the
// trigger's own ancestor chain: the Prime Contacts table wraps its rows in
// an `overflow-x-auto` container (needed for horizontal scroll on narrow
// screens), and per the CSS overflow spec, setting one axis to something
// other than `visible` forces the other axis to clip too — silently
// cutting off an absolutely-positioned tooltip on any row near the top of
// the table. A portal sidesteps that ancestor entirely.
export default function HoverCard({ trigger, children, accent = "border-border", widthPx = 256 }: Props) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [entered, setEntered] = useState(false);

  const show = () => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ top: rect.top - 10, left: rect.left + rect.width / 2 });
  };
  const hide = () => {
    setPos(null);
    setEntered(false);
  };

  // Two-step mount so the opacity/scale transition actually animates from
  // its initial state instead of snapping straight to the final one.
  useLayoutEffect(() => {
    if (!pos) return;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [pos]);

  return (
    <span
      ref={anchorRef}
      className="relative inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {trigger}
      {pos &&
        createPortal(
          <span
            className={`fixed z-50 pointer-events-none -translate-x-1/2 -translate-y-full origin-bottom transition-all duration-150 ease-out ${
              entered ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
            style={{ top: pos.top, left: pos.left, width: widthPx }}
          >
            <span className={`block rounded-xl border ${accent} bg-surface text-foreground shadow-lg px-3.5 py-3 text-left`}>
              {children}
            </span>
            <span className={`absolute top-full left-1/2 -translate-x-1/2 -mt-[5px] w-2.5 h-2.5 rotate-45 border-b border-r ${accent} bg-surface`} />
          </span>,
          document.body
        )}
    </span>
  );
}
